/*
Author: Hoi Tak Leung
Email: hoi.leung@greedy.mx
Last Update: 21/09/2023
*/

import React, { useState } from "react";
import {
  Typography,
  Container,
  CssBaseline,
  Grid,
  Box,
  InputAdornment,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";

import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import SendIcon from "@mui/icons-material/Send";

import "./ContactSection.css";
import { Form, Formik } from "formik";
import axios from "axios";
import * as Yup from "yup";
import { reportGtagConversion } from "../../../utils/tracking";
import { useScrollReveal } from "../../../helpers/ScrollReveal";
import { useLanguageContext } from "../../../context/languageContext";
import { textlanguages } from "../../../data/textOfThePage";

export default function ContactSection() {
  const { languageSelected } = useLanguageContext();
  useScrollReveal([
    {
      selector: ".revealContact",
      options: {
        distance: "100px",
        duration: 1000,
        easing: "ease-out",
        origin: "left",
        reset: false,
        interval: 500,
      },
    },
  ]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/send-email`;

      const response = await axios.post(apiUrl, values, {
        headers: { "Content-Type": "application/json" },
      });
      if (response.status === 200) {
        reportGtagConversion();
        setSnackbar({
          open: true,
          message: "Mensaje enviado con éxito!",
          severity: "success",
        });
        resetForm();
      } else {
        setSnackbar({
          open: true,
          message: "Error al enviar el mensaje.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Hubo un problema al enviar tu mensaje.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Container
      disableGutters
      maxWidth={false}
      className="section-contact-container"
    >
      <CssBaseline />
      <Formik
        initialValues={{
          fullName: "",
          email: "",
          company: "",
          phone: "",
          message: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={true}
      >
        {({
          isSubmitting,
          errors,
          touched,
          handleChange,
          handleBlur,
          values,
        }) => {
          return (
            <Form>
              <Grid container className="section-content">
                <Grid item xs={1} lg={1} xl={2}></Grid>
                <Grid item xs={10} lg={10} xl={8}>
                  <Box className="contact-form-container">
                    <Typography
                      variant="h3"
                      className="contact-form-header revealContact"
                    >
                      {textlanguages[languageSelected].contact.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      className="contact-form-description revealContact"
                    >
                      {textlanguages[languageSelected].contact.description}
                    </Typography>
                    <Grid container className="contact-form-grid">
                      <Grid
                        item
                        container
                        lg={7}
                        xl={7}
                        sx={{ borderRight: { lg: "1px solid gray" } }}
                        className="revealContact"
                      >
                        <Grid item xs={12} lg={6} xl={6} className="form-field">
                          <TextField
                            fullWidth
                            variant="standard"
                            label={`${textlanguages[languageSelected].contact.nameField}`}
                            name="fullName"
                            onChange={handleChange}
                            value={values.fullName}
                            onBlur={handleBlur}
                            error={touched.fullName && !!errors.fullName}
                            helperText={
                              touched.fullName && errors.fullName
                                ? errors.fullName
                                : " "
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AccountCircleOutlinedIcon
                                    sx={{ color: "#0065a1" }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} lg={6} xl={6} className="form-field">
                          <TextField
                            fullWidth
                            variant="standard"
                            label={`${textlanguages[languageSelected].contact.emailField}`}
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.email && !!errors.email}
                            helperText={
                              touched.email && errors.email ? errors.email : " "
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailOutlinedIcon
                                    sx={{ color: "#0065a1" }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} lg={6} xl={6} className="form-field">
                          <TextField
                            fullWidth
                            variant="standard"
                            label={`${textlanguages[languageSelected].contact.companyField}`}
                            name="company"
                            value={values.company}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.company && !!errors.company}
                            helperText={
                              touched.company && errors.company
                                ? errors.company
                                : " "
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <WorkOutlineOutlinedIcon
                                    sx={{ color: "#0065a1" }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} lg={6} xl={6} className="form-field">
                          <TextField
                            fullWidth
                            variant="standard"
                            label={`${textlanguages[languageSelected].contact.phoneField}`}
                            name="phone"
                            type="tel"
                            value={values.phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.phone && !!errors.phone}
                            helperText={
                              touched.phone && errors.phone ? errors.phone : " "
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocalPhoneOutlinedIcon
                                    sx={{ color: "#0065a1" }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} className="form-field">
                          <TextField
                            fullWidth
                            name="message"
                            id="standard-multiline-static"
                            label={`${textlanguages[languageSelected].contact.messageFiled}`}
                            value={values.message}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.message && !!errors.message}
                            helperText={
                              touched.message && errors.message
                                ? errors.message
                                : " "
                            }
                            multiline
                            rows={4}
                            variant="standard"
                          />
                        </Grid>
                        <Grid item xs={12} className="form-field">
                          <Button
                            className="btn-send-form"
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            startIcon={<SendIcon />}
                          >
                            {`${textlanguages[languageSelected].contact.ButtonSend}`}
                          </Button>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        lg={5}
                        xl={5}
                        className="contact-box-container revealContact"
                      >
                        <Box className="contact-box">
                          <Box sx={{ paddingBottom: "2vh" }}>
                            <Typography variant="h6" fontWeight={"bold"}>
                              {`${textlanguages[languageSelected].contact.emailInfo}`}
                            </Typography>
                            <Typography variant="body">
                              contacto@greedy.mx
                            </Typography>
                          </Box>
                          <Box sx={{ paddingBottom: "2vh" }}>
                            <Typography variant="h6" fontWeight={"bold"}>
                              {`${textlanguages[languageSelected].contact.phoneInfo}`}
                            </Typography>
                            <Typography variant="body">
                              +52 55 12227016
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              fontWeight={"bold"}
                            >{`${textlanguages[languageSelected].contact.addressInfo}`}</Typography>
                            <Typography variant="body">
                              P.º de Los Framboyanes 129 Paseos de Taxqueña,
                              Coyoacán, 04250 Ciudad de México, CDMX
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={1} lg={1} xl={2}></Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

const validationSchema = Yup.object({
  fullName: Yup.string()
    .max(60, "Máximo de 60 caracteres")
    .min(3, "Minimo de 3 caracteres")
    .required("El nombre completo es obligatorio"),
  email: Yup.string()
    .email("Formato de correo electrónico inválido")
    .min(5, "Minimo de 5 caracteres")
    .max(100, "Máximo de 100 caracteres")
    .required("El correo electrónico es obligatorio"),
  company: Yup.string()
    .min(2, "Minimo de 2 caracteres")
    .max(50, "Máximo de 50 caracteres")
    .required("La compañía es obligatoria"),
  message: Yup.string()
    .min(10, "El mensaje debe tener al menos 10 caracteres")
    .max(1000, "Máximo de 1000 caracteres")
    .required("El mensaje es obligatorio"),
  phone: Yup.string()
    .matches(/^[0-9+\-\s()]*$/, "Solo números y símbolos válidos")
    .min(8, "El número debe de tener al menos 8 caracteres")
    .max(20, "Máximo 20 caracteres")
    .required("El teléfono es obligatorio"),
});
